import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Upload, Download, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';
import { parseCsv } from '@/utils/csv-helper';

/**
 * Bulk import of leads from a CSV.
 *
 * The result is reported ROW BY ROW. A list bought from a directory is full of numbers already in
 * the pipeline, and the only useful answer to "500 rows, 12 skipped" is which twelve and why —
 * silently dropping them is how the same prospect ends up called by two people, or not at all.
 */
export function LeadImportDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<{ created: number; total: number; skipped: { row: number; company: string; reason: string }[] } | null>(null);

  const pick = async (file?: File | null) => {
    if (!file) return;
    setResult(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      const { rows: parsed } = parseCsv(text);
      setRows(parsed);
      if (!parsed.length) toast.error('That file has no data rows.');
    } catch {
      toast.error('Could not read that file — save it as CSV and try again.');
    }
  };

  const run = useMutation({
    mutationFn: async () => (await api.post('/leads/import', { rows })).data,
    onSuccess: (r: any) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead-dashboard'] });
      toast.success(`${r.created} lead${r.created === 1 ? '' : 's'} imported`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Import failed'),
  });

  return (
    <div className="fixed inset-0 z-[900] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-lg font-semibold">Import Leads</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800 rounded p-3">
            <p className="text-xs text-gray-600">
              Start from the template — its headings are the ones the importer reads. Extra columns are ignored,
              and <strong>Company Name</strong> is the only one that must be filled.
            </p>
            <button onClick={() => downloadBlob('/leads/import-template', 'leads-import-template.csv')}
              className="px-3 py-1.5 border rounded text-xs inline-flex items-center gap-1 whitespace-nowrap">
              <Download size={13} /> Template
            </button>
          </div>

          <label className="block border-2 border-dashed rounded p-6 text-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
            <Upload className="mx-auto text-slate-400 mb-1" size={22} />
            <div className="text-sm text-slate-700">{fileName || 'Choose a CSV file'}</div>
            <div className="text-xs text-gray-400">{rows.length ? `${rows.length} rows ready` : 'or drop it here'}</div>
          </label>

          {result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 size={16} /> {result.created} of {result.total} rows imported.
              </div>
              {result.skipped.length > 0 && (
                <div className="border rounded">
                  <div className="px-3 py-2 bg-amber-50 text-amber-800 text-xs flex items-center gap-2 border-b">
                    <AlertTriangle size={14} /> {result.skipped.length} row{result.skipped.length === 1 ? '' : 's'} skipped — nothing was changed for these.
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr><th className="text-left px-3 py-1">Row</th><th className="text-left px-3 py-1">Company</th><th className="text-left px-3 py-1">Why</th></tr>
                      </thead>
                      <tbody>
                        {result.skipped.map((s, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-1">{s.row}</td>
                            <td className="px-3 py-1">{s.company || '—'}</td>
                            <td className="px-3 py-1 text-gray-600">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 border rounded text-sm">{result ? 'Close' : 'Cancel'}</button>
          <button onClick={() => run.mutate()} disabled={!rows.length || run.isPending}
            className="px-4 py-2 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {run.isPending && <Loader2 size={14} className="animate-spin" />} Import {rows.length ? `${rows.length} rows` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
